/* eslint-disable @typescript-eslint/no-use-before-define */

import React, { useEffect, useMemo, useState } from 'react';
import { Title, Text, Link, Space, Modal, Input, Checkbox } from 'components';
import { LocationsDTO } from '@bb/domain/dist/types/dto/OrgController/locations';
import { SearchOutlined } from '@ant-design/icons';
import { INotificationSettings } from '@bb/domain/dist/types';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { cloneDeep } from 'lodash';
import styled from 'styled-components';
import { StyledModal, StyledModalWidth } from './constants';

type BranchFilterProps = {
  locations: LocationsDTO['locations'];
  disabledLocations: INotificationSettings['disabled_locations'];
  setModifiedNotifications: React.Dispatch<
    React.SetStateAction<INotificationSettings>
  >;
};

type LocationTypeWithNotification = LocationsDTO['locations'][number] & {
  hasNotification: boolean;
};

const SELECT_ALL_NAME = 'Select all';

const BranchFilter: React.FC<BranchFilterProps> = ({
  locations,
  disabledLocations,
  setModifiedNotifications,
}) => {
  const [editOpen, setEditOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [modifiedDisabledLocations, setModifiedDisabledLocations] = useState(
    cloneDeep(disabledLocations),
  );

  useEffect(() => {
    // Reinitialize when the modal is open / closed
    setModifiedDisabledLocations(cloneDeep(disabledLocations));
  }, [editOpen, disabledLocations]);

  const subscribedLocationsText = useMemo(
    () =>
      locations
        .filter(
          ({ isDeleted }, index) => !disabledLocations[index] && !isDeleted,
        )
        .map(({ name }) => name)
        .join(', ') || 'None',
    [locations, disabledLocations],
  );

  const locationsWithNotifications: LocationTypeWithNotification[] =
    useMemo(() => locations.map((value, index) => ({ ...value, hasNotification: !modifiedDisabledLocations[index] })), [locations, modifiedDisabledLocations]);

  const selectAllLocations: LocationTypeWithNotification = useMemo(
    () => ({
      name: SELECT_ALL_NAME,
      // @ts-ignore types don't match
      addresses: [],
      hasNotification: locationsWithNotifications.every(
        ({ hasNotification, isDeleted }) => hasNotification || isDeleted,
      ),
    }),
    [locationsWithNotifications],
  );

  const onChange =
    (name: string, index: number) =>
    ({ target: { checked } }: CheckboxChangeEvent) =>
      setModifiedDisabledLocations(prev => ({
        ...prev,
        ...(name === SELECT_ALL_NAME
          ? locations
              .map<[(typeof locations)[number], number]>((val, i) => [val, i])
              .reduce(
                (acc, [{ isDeleted: _isDeleted }, i]) => {
                  acc[i] = Boolean(_isDeleted) || !checked;
                  return acc;
                },
                {} as typeof prev,
              )
          : { [index - 1]: !checked }),
      }));

  const onOk = () => {
    setSearchText('');
    setModifiedNotifications(prev => ({
      ...prev,
      disabled_locations: modifiedDisabledLocations,
    }));

    setEditOpen(false);
  };

  return (
    <>
      <Space direction="vertical" size={'large'}>
        <Space direction="vertical">
          <Title level={3}>Branches</Title>

          <Text>
            You can choose the branches from which you would like to receive the
            notifications below:
          </Text>
        </Space>

        <Space direction="horizontal" size={'middle'} align={'start'}>
          <Text type="secondary">Subscribed: {subscribedLocationsText}</Text>
          <Link onClick={() => setEditOpen(true)} nowrap>
            Edit
          </Link>
        </Space>
      </Space>
      <StyledModal
        title="Branches"
        width={StyledModalWidth}
        open={editOpen}
        destroyOnClose
        onCancel={() => {
          setSearchText('');
          setEditOpen(false);
        }}
        onOk={onOk}
      >
        <Space direction="vertical" size={'middle'}>
          <Text>
            Select the branches from which you would like to receive
            notifications:
          </Text>

          <Input
            placeholder="Search branches"
            onChange={event => setSearchText(event.currentTarget.value)}
            suffix={<SearchOutlined />}
          />

          <Scrollable>
            <Space direction="vertical" size={'small'}>
              {[selectAllLocations, ...locationsWithNotifications].map(
                ({ name, isDeleted, hasNotification }, index) => {
                  // filter deleted out
                  if (isDeleted) return null;

                  // filter by search text
                  if (
                    searchText &&
                    !name.toLowerCase().includes(searchText.toLowerCase())
                  ) {
                    return null;
                  }

                  return (
                    <Checkbox
                      key={index}
                      checked={hasNotification}
                      onChange={onChange(name, index)}
                    >
                      {name}
                    </Checkbox>
                  );
                },
              )}
            </Space>
          </Scrollable>
        </Space>
      </StyledModal>
    </>
  );
};

export default BranchFilter;

const Scrollable = styled.div`
  max-height: 300px;
  overflow-y: auto;
`;
